import { ref } from 'vue';
import { useAppState } from './useAppState';
import { getSpendingAnalysis } from './useSpendingAnalysis';
export function useSpendingPlansSidebar(registerSidebar = null) {
    const state = useAppState();
    const spendingPlansSidebarItems = ref([]);
    const selectedPlanId = ref(null);
    function fetchAndRegisterSpendingPlans(handlers) {
        // The analysis already carries each category's plan (budget) and totalOut
        // (spent) for the current cycle — same source SpendingPlanPage builds from.
        getSpendingAnalysis(null, null, { snapToSpendingCycle: true }, (result) => {
            buildSpendingPlansSidebarItems(result);
            registerSpendingPlansSidebar(handlers);
        });
    }
    function buildSpendingPlansSidebarItems(analysis) {
        const items = [];
        const cats = analysis?.categories ?? [];
        for (const c of cats) {
            if (!c.plan)
                continue;
            const budget = c.plan.amount || 0;
            if (!budget)
                continue;
            const cls = c.plan.mgClassification;
            const classId = cls?.id;
            const spent = Math.abs(Math.round(c.totalOut || 0));
            const percent = Math.min(100, Math.round((spent / budget) * 100));
            items.push({
                id: `plan-${classId}`,
                title: c.subCategory ?? cls?.fullName ?? 'Plan',
                subtitle: `${state.formatter.format(spent)} / ${state.formatter.format(budget)}`,
                progress: percent,
                colour: percent >= 100 ? '#c62828' : percent >= 80 ? '#f9a825' : '#2e7d32',
                _planData: {
                    classificationId: classId,
                    name: c.subCategory ?? cls?.fullName,
                    iconPath: cls?.iconPath,
                    budget,
                    spent,
                    remaining: budget - spent,
                    percent,
                },
            });
        }
        spendingPlansSidebarItems.value = items;
    }
    function registerSpendingPlansSidebar(handlers) {
        if (!registerSidebar)
            return;
        registerSidebar({
            config: {
                actionButton: { icon: 'mdi-tune-variant', label: 'Manage plans' },
                pageItemsTitle: 'Spending Plans',
                pageItemsEmptyText: 'No plans set',
                pageItems: spendingPlansSidebarItems.value,
                currentPageItemId: selectedPlanId.value
            },
            handlers: {
                onActionClick: () => { if (handlers?.onManagePlans)
                    handlers.onManagePlans(); },
                onPageItemClick: (id) => { handleSpendingPlanClick(id, handlers?.onSpendingPlanSelected); },
                onPageItemDelete: () => { },
                onPageItemRename: () => { },
                onOpenSettings: () => { if (handlers?.onOpenSettings)
                    handlers.onOpenSettings(); }
            }
        });
    }
    function handleSpendingPlanClick(planId, onSpendingPlanSelected) {
        if (!String(planId).startsWith('plan-'))
            return;
        const item = spendingPlansSidebarItems.value.find((i) => i.id === planId);
        if (!item)
            return;
        selectedPlanId.value = planId;
        // Call the page's handler if it exists
        if (typeof onSpendingPlanSelected === 'function') {
            onSpendingPlanSelected(item._planData);
        }
        registerSpendingPlansSidebar({ onSpendingPlanSelected });
    }
    function clearSpendingPlanSelection() {
        selectedPlanId.value = null;
        registerSpendingPlansSidebar();
    }
    function getSpendingPlanData(planId) {
        const item = spendingPlansSidebarItems.value.find((i) => i.id === planId);
        return item?._planData || null;
    }
    return {
        spendingPlansSidebarItems,
        selectedPlanId,
        fetchAndRegisterSpendingPlans,
        buildSpendingPlansSidebarItems,
        registerSpendingPlansSidebar,
        handleSpendingPlanClick,
        clearSpendingPlanSelection,
        getSpendingPlanData,
    };
}
